import { useState, useRef } from 'react';
import { useProperty, useTranslation } from '@eti/providers';
import { constants, price as priceUtils } from '@eti/utils';
import { mediaQueries, css } from '@eti/styles';
import { ConnectedSelectBox } from '@eti/form';
import { Inline, SelectBox, Text, vars } from '@etg/wings';
import { AgeType } from '@eti/schema-types';
import { Check } from '@phosphor-icons/react';
import checkIcon from '@eti/assets/icons/checkList.svg?url'; // eslint-disable-line import/no-unresolved
import { screenReaderOnly } from '../../common/utils/styles';
import { useMutationObserver } from '../../custom-hooks/useMutationObserver';
import getTranslatedTexts from '../../common/text-resolvers/utils/getTranslatedTexts';
import { FLEXIBLE_TICKET } from '../../products';
import { getOptions } from '../utils/flexibleTicketTransformer';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { formatLabel, formatOptions, getMinPrice, getTotalPrice, handleSelectedOption, SELECTBOX_VALUE } from '../../common/utils/selectBoxOptions';
import ProductModal from '../../common/components/ProductModal';
import { isEnabled as isModalEnabled } from '../../common/utils/modal';
import { useIllustration } from '../../common/utils/useIllustration';
import { useDynamicIcon } from '../../common/utils/dynamic-icons/useDynamicIcon';
import { DYNAMIC_ICON } from '../../common/utils/dynamic-icons/icons';
import { getReadMoreLabel, getUpsellProps } from '../../common/utils/productCardProps';
import { getAgeTypeLabel } from '../../common/utils/ageTypes';
import { cleanText, TRACKING_PROPERTIES, TRACKING_VALUES, useProductSnowplowTracking } from '../../custom-hooks/useProductSnowplowTracking';
import { useLinksAccessibility } from '../../custom-hooks/useLinkAccessibility';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = FLEXIBLE_TICKET;
const {
  MOUSE_FLOW_CLASS_NAMES
} = constants;
const texts = {
  adultLabel: 'Adult',
  allTravelersLabel: 'Product.FlexibleTicket.Select.All',
  childLabel: 'Child',
  infantNoteLabel: 'TravelerDetails.IncludedInfant.Text.Default',
  noTravelersLabel: 'Product.FlexibleTicket.Select.None',
  notSelectedLabel: 'Products.Price.Label.From',
  persuasionContent: 'flexibleTicketPersuasionContent',
  placeholder: 'Product.FlexibleTicket.Dropdown.Button.Text',
  placeholderSelectedPlural: 'Product.FlexibleTicket.Dropdown.Label.Selected.Plural',
  placeholderSelectedSingular: 'Product.FlexibleTicket.Dropdown.Label.Selected.Singular',
  readMoreContent: 'flexibleTicketReadMoreModalContent',
  selectboxLabel: 'Product.SelectOneValidation.Message',
  text: 'flexibleTicketAbstract',
  title: 'flexibleTicketHeaderTitle',
  totalLabel: 'Products.Price.Label.Total',
  upSell: 'flexibleTicketUpSellText',
  dropDownCloseInstruction: 'ScreenReader.Product.CloseDropdown.Instruction.Text'
};
const infantNoteStyles = css`
  grid-area: 4 / 1 / 5 / 4;
  margin-top: 16px;

  & > svg {
    flex-shrink: 0;
    height: 16px;
    transform: translateY(2px);
    width: 16px;
  }
`;
const selectBoxOptionLabelStyles = css`
  justify-content: space-between;
  width: 100%;

  & > span:last-of-type {
    white-space: nowrap;
  }
`;
const flexibleTicketCtaGridLayoutStyles = isSelectboxLabel => css`
  column-gap: 8px;
  display: grid;
  grid-template-columns: 2fr auto;
  grid-template-rows: repeat(3, auto);

  @media ${mediaQueries.medium.up} {
    justify-content: unset;
  }

  .priceLabel {
    grid-area: 3 / 1 / 3 / 2;
    margin-top: ${isSelectboxLabel ? '24px' : '4px'};

    @media ${mediaQueries.small.only} {
      grid-area: 2 / 3 / 2 / 4;
    }
  }

  .price {
    grid-area: 4 / 1 / 4 / 2;

    @media ${mediaQueries.small.only} {
      grid-area: 3 / 3 / 4 / 4;
    }
  }
`;
const textContentStyles = iconContent => css`
  grid-area: 1 / 1 / 2 / 4;

  > *:not(:last-child) {
    margin-bottom: 16px;
  }

  & p {
    font-size: 0.875rem;

    @media (min-width: 48em) {
      line-height: 150%;
    }
  }

  & li {
    padding-inline-start: 24px;
    position: relative;

    [dir='rtl'] & {
      padding-left: 0;
    }

    &::before {
      background: url(${checkIcon});
      background-repeat: no-repeat;
      background-size: contain;
      content: '';
      height: 16px;
      inset-inline-start: 0;
      margin-inline-end: 8px;
      position: absolute;
      top: 3px;
      width: 16px;
    }
  }

  & a {
    color: ${vars.colors.links.default};
  }

  & a::after {
    bottom: 6px;
    content: ${iconContent !== null && iconContent !== void 0 ? iconContent : ''};
    display: inline-block;
    height: 16px;
    margin-inline-start: 4px;
    position: relative;
    vertical-align: bottom;
    width: 16px;
  }
`;
const selectBoxStyles = css`
  display: grid;
  grid-area: 3 / 2 / 6 / 4;

  button {
    span {
      pointer-events: none;
    }
  }

  label {
    margin-inline-start: 0;

    @media ${mediaQueries.small.only} {
      text-wrap: wrap;
      width: 80%;
    }
  }

  @media ${mediaQueries.small.only} {
    grid-area: 3 / 1 / 4 / 4;
  }

  @media ${mediaQueries.medium.up} {
    width: 320px;
  }

  @media ${mediaQueries.large.up} {
    width: 272px;
  }

  @media ${mediaQueries.xlarge.up} {
    width: 336px;
  }
`;
const optionListStyles = css`
  li:first-child {
    font-weight: 800;
  }

  span,
  div {
    pointer-events: none;
  }
`;
const FlexibleTicket = _ref => {
  var _order$travelers, _getOptions;
  let {
    form,
    order,
    initialState,
    travelerNames,
    onModalConfirm,
    productPosition
  } = _ref;
  const [selectedOptions, setSelectedOptions] = useState(initialState);
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const illustrationUrl = useIllustration(PRODUCT_NAME);
  const iconColor = vars.colors.signal.positive.light;
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  const rootElement = useRef(null);
  const {
    sendClickEvent,
    sendAddToCartEvent,
    updateContext
  } = useProductSnowplowTracking(PRODUCT_NAME, {
    productPosition,
    rootElement
  });
  const {
    adultLabel,
    allTravelersLabel,
    childLabel,
    infantNoteLabel,
    noTravelersLabel,
    notSelectedLabel,
    persuasionContent,
    placeholder,
    placeholderSelectedPlural,
    placeholderSelectedSingular,
    readMoreContent,
    selectboxLabel,
    text,
    title,
    totalLabel,
    upSell,
    dropDownCloseInstruction
  } = getTranslatedTexts(texts, t);
  const isValidationEnabled = p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');
  const [isOpen, setIsOpen] = useState(false);
  const hasLabel = Boolean(isValidationEnabled && selectboxLabel);
  const hasInfants = order === null || order === void 0 || (_order$travelers = order.travelers) === null || _order$travelers === void 0 ? void 0 : _order$travelers.some(traveler => traveler.ageType === AgeType.Inf);
  const selectBoxTestId = `${FLEXIBLE_TICKET.name}-select-box`;
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });
  const options = (_getOptions = getOptions({
    id: PRODUCT_ID,
    order,
    travelerNames,
    defaultTravelerLabel: t('TravelerDescription.Header')
  })) === null || _getOptions === void 0 || (_getOptions = _getOptions.filter(_ref2 => {
    let {
      ageType
    } = _ref2;
    return ageType !== AgeType.Inf;
  })) === null || _getOptions === void 0 ? void 0 : _getOptions.map(_ref3 => {
    let {
      ageType,
      id,
      name,
      priceRaw
    } = _ref3;
    return {
      ageType,
      id,
      name: `${name}, ${getAgeTypeLabel(ageType, adultLabel, childLabel, '')}`,
      priceRaw
    };
  });
  const productPriceRaw = !(selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.length) ? getMinPrice(options) : getTotalPrice(selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.filter(option => !(option !== null && option !== void 0 && option.exclude)));
  const {
    upSellObject,
    isUpsellHighlight = false,
    shouldDisplayUpsell = false
  } = getUpsellProps(upSell);
  const readMoreButtonLabel = getReadMoreLabel(t);
  const productData = {
    extraProps: null,
    form,
    illustrationUrl,
    isHighlighted: isUpsellHighlight,
    isSelected: null,
    price: priceUtils.formatPrice(productPriceRaw),
    priceLabel: !(selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.length) ? notSelectedLabel : totalLabel,
    priceRaw: productPriceRaw !== null && productPriceRaw !== void 0 ? productPriceRaw : 0,
    productId: PRODUCT_ID,
    productName: PRODUCT_NAME,
    readMoreButtonLabel,
    readMoreContent,
    shouldDisplayUpsell,
    title,
    travelers: order.travelers,
    upSell: upSellObject
  };
  const formattedOptions = formatOptions(options, allTravelersLabel, noTravelersLabel);
  const formatSelectedOption = opts => {
    const label = formatLabel({
      selectedOptions: opts,
      allTravelersLabel,
      defaultPlaceholder: placeholder,
      placeholderSelectedSingular,
      placeholderSelectedPlural,
      noTravelersLabel
    });
    return /*#__PURE__*/_jsx("p", {
      children: label
    });
  };
  const handleModalConfirmation = async () => {
    const confirmationValues = formattedOptions.filter(_ref4 => {
      let {
        value
      } = _ref4;
      return value !== 'NONE';
    });
    if ((confirmationValues === null || confirmationValues === void 0 ? void 0 : confirmationValues.length) > 0) {
      setSelectedOptions(confirmationValues);
      onModalConfirm(form, `${FLEXIBLE_TICKET.name}`, confirmationValues);
      await updateContext({
        quantity: formattedOptions.length - 2
      });
      sendAddToCartEvent({
        addToCart: true,
        property: TRACKING_PROPERTIES.VALIDATION_POP_UP
      });
    }
    setIsOpen(false);
  };
  const handleModalOpen = selectedOption => {
    const canOpenModal = isModalEnabled(order.persuasion) && selectedOption.find(option => option.value === SELECTBOX_VALUE.NONE);
    if (canOpenModal) {
      setIsOpen(true);
    }
  };

  // Add ARIA roles and attributes to enhance screen reader usability when the dropdown menu opens.
  useMutationObserver({
    targetElement: rootElement,
    selector: "[role='listbox']",
    onElementAdded: element => {
      element.setAttribute('aria-describedby', 'dropdown-close-instruction');
      sendClickEvent({
        property: TRACKING_PROPERTIES.VIEW_OPTIONS
      });
    }
  });
  const sendSnowplowTracking = async (_ref5, _ref6) => {
    var _currentTarget$getAtt;
    let {
      currentTarget
    } = _ref5;
    let {
      value: selectedOption
    } = _ref6;
    const isOptionAlreadySelected = (_currentTarget$getAtt = currentTarget.getAttribute('data-headlessui-state')) === null || _currentTarget$getAtt === void 0 ? void 0 : _currentTarget$getAtt.includes('selected');
    const isAllTravelersSelected = selectedOption === SELECTBOX_VALUE.ALL;
    const isNoneTravelerSelected = selectedOption === SELECTBOX_VALUE.NONE;
    const isSingleTravelerSelected = !isAllTravelersSelected && !isNoneTravelerSelected;
    if (isAllTravelersSelected && !isOptionAlreadySelected) {
      await updateContext({
        quantity: formattedOptions.length - 2
      });
      sendAddToCartEvent({
        addToCart: true,
        value: TRACKING_VALUES.ALL_PASSENGERS
      });
    } else if (isNoneTravelerSelected && !isOptionAlreadySelected) {
      const totalPrice = getTotalPrice(options === null || options === void 0 ? void 0 : options.filter(option => ({
        price: option === null || option === void 0 ? void 0 : option.price
      })));
      const formattedPrice = cleanText(priceUtils.formatPrice(totalPrice));
      await updateContext({
        quantity: options.length,
        price: formattedPrice
      });
      sendAddToCartEvent({
        addToCart: false
      });
    } else if (isSingleTravelerSelected) {
      await updateContext({
        quantity: 1
      });
      sendAddToCartEvent({
        addToCart: !isOptionAlreadySelected,
        value: TRACKING_VALUES.SINGLE_PASSENGER
      });
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(ProductLayout, {
      productData: productData,
      ref: rootElement,
      children: [/*#__PURE__*/_jsx(ProductLayout.Body, {
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [text && /*#__PURE__*/_jsx(Text, {
            asHtml: true,
            className: textContentStyles(icon),
            children: text
          }), hasInfants && infantNoteLabel && /*#__PURE__*/_jsxs(Inline, {
            className: infantNoteStyles,
            noWrap: true,
            spacing: 8,
            children: [/*#__PURE__*/_jsx(Check, {
              color: iconColor,
              "data-testid": "included-checks"
            }), /*#__PURE__*/_jsx(Text, {
              children: infantNoteLabel
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(ProductLayout.Cta, {
        customGrid: flexibleTicketCtaGridLayoutStyles(Boolean(selectboxLabel)),
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            "aria-hidden": true,
            className: screenReaderOnly,
            id: "dropdown-close-instruction",
            tabIndex: -1,
            children: dropDownCloseInstruction
          }), /*#__PURE__*/_jsx(ConnectedSelectBox, {
            className: selectBoxStyles,
            "data-testid": selectBoxTestId,
            formatSelectedOption: formatSelectedOption,
            isDisabled: !(formattedOptions !== null && formattedOptions !== void 0 && formattedOptions.length),
            isMultiple: true,
            label: hasLabel ? selectboxLabel : '',
            name: `${FLEXIBLE_TICKET.name}.value`,
            normalize: (newValue, prevVal) => {
              if (!newValue) {
                return selectedOptions;
              }
              const selectedOption = handleSelectedOption(formattedOptions, prevVal, newValue);
              if (selectedOption !== null && selectedOption !== void 0 && selectedOption.length) {
                setSelectedOptions(selectedOption);
                handleModalOpen(selectedOption);
                return selectedOption;
              }
              setSelectedOptions(null);
              return null;
            },
            placeholder: placeholder,
            shouldMarkInputAsRequired: hasLabel,
            validationRuleName: FLEXIBLE_TICKET.name,
            children: /*#__PURE__*/_jsx("ul", {
              className: optionListStyles,
              children: formattedOptions === null || formattedOptions === void 0 ? void 0 : formattedOptions.map(option => /*#__PURE__*/_jsx(SelectBox.Option, {
                isMultiple: true,
                label: /*#__PURE__*/_jsxs(Inline, {
                  className: selectBoxOptionLabelStyles,
                  noWrap: true,
                  spacing: 4,
                  children: [/*#__PURE__*/_jsx("span", {
                    className: MOUSE_FLOW_CLASS_NAMES,
                    children: option.label
                  }), /*#__PURE__*/_jsx("span", {
                    children: option.price
                  })]
                }),
                onClick: event => sendSnowplowTracking(event, option),
                value: option
              }, option.value))
            })
          })]
        })
      })]
    }), /*#__PURE__*/_jsx(ProductModal, {
      content: persuasionContent,
      isOpen: isOpen,
      onConfirm: handleModalConfirmation,
      onDecline: () => {
        sendAddToCartEvent({
          addToCart: false,
          property: TRACKING_PROPERTIES.VALIDATION_POP_UP
        });
        setIsOpen(false);
      },
      productName: FLEXIBLE_TICKET.name,
      title: title
    })]
  });
};
export default FlexibleTicket;
//# sourceMappingURL=FlexibleTicket.js.map